import type React from 'react';

import { colors } from '@packages/core/styles';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    tooltip: {
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '16px',
            padding: '4px 8px',
            textAlign: 'left',
        },
    },
});

export type ToolTipOverlayProps = {
    /** The element to wrap with a tooltip. */
    children: React.ReactElement;

    /** CSS class name applied to the tooltip overlay. Falls back to the standard tooltip styling. */
    className?: string;

    /** Placement of the tooltip relative to the trigger element. */
    placement?: 'bottom' | 'left' | 'right' | 'top';

    /** Tooltip text; when absent, no tooltip is rendered and children are returned directly. */
    text?: string;

    /** Unique ID for the tooltip element (required by react-bootstrap). */
    tooltipId: string;
};

/**
 * Conditional tooltip wrapper. When tooltip text is provided, wraps the children
 * in a react-bootstrap OverlayTrigger; otherwise renders children directly.
 *
 * @param props - component props
 * @returns the children, optionally wrapped with a tooltip overlay
 */
export function ToolTipOverlay({ children, className, placement = 'bottom', text, tooltipId }: ToolTipOverlayProps) {
    const classes = useStyles();

    if (!text) {
        return children;
    }

    return (
        <OverlayTrigger
            overlay={
                <Tooltip className={className || classes.tooltip} id={tooltipId}>
                    {text}
                </Tooltip>
            }
            placement={placement}
        >
            <span>{children}</span>
        </OverlayTrigger>
    );
}
